import { compareDecimalStrings } from "./decimal";

export type SortDir = "asc" | "desc";

export interface SortState<Key extends string> {
  key: Key;
  dir: SortDir;
}

export interface SortRule<Row> {
  isNull: (row: Row) => boolean;
  compare: (a: Row, b: Row) => number;
}

// `compare` only ever runs on two non-null rows, which is what makes the cast safe.
const rule = <Row, Value>(
  valueOf: (row: Row) => Value | null,
  compare: (a: Value, b: Value) => number,
): SortRule<Row> => ({
  isNull: (row) => valueOf(row) === null,
  compare: (a, b) => compare(valueOf(a) as Value, valueOf(b) as Value),
});

// `NaN` is folded into the null branch: an unparseable date reaches here as one, and a
// comparator that returns `NaN` scrambles the order of the whole array, not just its row.
export const byNumber = <Row>(valueOf: (row: Row) => number | null) =>
  rule(
    (row: Row) => {
      const value = valueOf(row);
      return value === null || Number.isNaN(value) ? null : value;
    },
    (a: number, b: number) => a - b,
  );

// The row still renders the original string; only the ordering goes through the text compare,
// which is what keeps a difference in the sixth decimal from being rounded away.
export const byDecimalString = <Row>(valueOf: (row: Row) => string | null) =>
  rule((row: Row) => {
    const value = valueOf(row);
    return value === null || value.trim() === "" ? null : value;
  }, compareDecimalStrings);

export const byText = <Row>(valueOf: (row: Row) => string | null) =>
  rule(
    (row: Row) => {
      const value = valueOf(row);
      return value === null || value.trim() === "" ? null : value;
    },
    (a: string, b: string) => a.localeCompare(b),
  );

export const sortRows = <Row extends { id: number }, Key extends string>(
  rows: Row[],
  rules: Record<Key, SortRule<Row>>,
  { key, dir }: SortState<Key>,
): Row[] => {
  const { isNull, compare } = rules[key];
  const direction = dir === "asc" ? 1 : -1;

  return [...rows].sort((a, b) => {
    const aIsNull = isNull(a);
    const bIsNull = isNull(b);

    // Null placement is settled before the direction multiplier so `desc` never lifts
    // a missing value to the top, and the id tie-break stays ascending either way.
    if (aIsNull || bIsNull) {
      if (aIsNull && bIsNull) {
        return a.id - b.id;
      }
      return aIsNull ? 1 : -1;
    }

    const primary = compare(a, b);
    return primary === 0 ? a.id - b.id : primary * direction;
  });
};
